import type { RootStore } from '../stores/root';
import { shouldRouteBackToLoginInApp } from '../stores/constants';
import {
  SEARCH_PARAMS,
  WIX_DEFAULT_ORGANIZATION_ID,
  WIX_PARENT_ACCOUNT_HINT,
} from './constants';

type WithParent = { parentAccountId?: string | null };

/**
 * An account's organization. The users API calls it parentAccountId and may
 * leave it out for an account directly under Wix.
 */
export const organizationIdOf = (account: WithParent): string =>
  account.parentAccountId || WIX_DEFAULT_ORGANIZATION_ID;

/**
 * Which of an email's several accounts the login asks for: the one in the
 * requested organization (an invite's, an IdP login's or the account
 * selector's pick), else
 * the older `?parentHint=` that other apps link with (`wix` for an account
 * directly under Wix), also when no account is in that organization.
 * Undefined when neither asks for one of them, so the selector asks again. A
 * parentHint that matches no account falls back to the first one, as it
 * always has.
 */
export const findRequestedAccount = <T extends WithParent>(
  accounts: T[],
  {
    organizationId,
    parentHint,
  }: { organizationId?: string; parentHint?: string },
): T | undefined => {
  const inOrganization =
    organizationId &&
    accounts.find((account) => organizationIdOf(account) === organizationId);
  if (inOrganization) {
    return inOrganization;
  }
  if (parentHint) {
    return (
      accounts.find(
        (account) =>
          (!account.parentAccountId &&
            parentHint === WIX_PARENT_ACCOUNT_HINT) ||
          account.parentAccountId === parentHint,
      ) ?? accounts[0]
    );
  }
  return undefined;
};

/**
 * The organization the account lookups of this email ask for: the one picked
 * in the account selector for it, else an invite's. Read only with the in-app
 * path on (shouldRouteBackToLoginInApp); otherwise the lookups narrow by
 * parentHint alone, as before.
 */
export const getRequestedOrganizationId = (
  rootStore: Pick<
    RootStore,
    'experiments' | 'navigationStore' | 'userDataStore'
  >,
  email: string,
): string | undefined =>
  (shouldRouteBackToLoginInApp(rootStore.experiments) &&
    (rootStore.userDataStore.pickedOrganizationIdFor(email) ||
      getInviteOrganizationId(rootStore))) ||
  undefined;

const GUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * The organization a login (and its passkey challenge) asks for: the
 * organization of the account picked in the account selector for this email,
 * or named by its IdP login, else an invite's, else the organization of the
 * account the lookup resolved. Each is taken only when it is a GUID, since the
 * server rejects anything else.
 */
export const getLoginOrganizationId = (
  rootStore: Pick<RootStore, 'navigationStore' | 'userDataStore'>,
  email?: string,
): string | undefined =>
  [
    rootStore.userDataStore.namedOrganizationIdFor(email),
    getInviteOrganizationId(rootStore),
    rootStore.userDataStore.organizationIdFor(email),
  ].find(
    (organizationId) => organizationId && GUID_PATTERN.test(organizationId),
  );

/**
 * The organization an invite link asks the user to join: the URL's
 * organizationId, when the URL also carries the invite's code. An
 * organizationId alone asks for nothing.
 */
export const getInviteOrganizationId = ({
  navigationStore,
}: Pick<RootStore, 'navigationStore'>): string | undefined => {
  const organizationId = navigationStore.getQueryParam(
    SEARCH_PARAMS.ORGANIZATION_ID,
  );
  const inviteCode = navigationStore.getQueryParam(
    SEARCH_PARAMS.ORGANIZATION_INVITE_CODE,
  );
  return inviteCode &&
    organizationId &&
    GUID_PATTERN.test(organizationId) &&
    organizationId !== WIX_DEFAULT_ORGANIZATION_ID
    ? organizationId
    : undefined;
};
